import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Tag,
  Typography,
  Upload,
  Image,
  Divider,
  Checkbox,
  Card,
  Popconfirm,
  Switch,
  Badge
} from 'antd';
import type { UploadProps } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  UploadOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
  PercentageOutlined
} from '@ant-design/icons';
import { specialPackageAPI } from '../../utils/api';
import { SpecialPackage, DayPlan } from '../../types';

const { Title, Text } = Typography;
const { TextArea } = Input;

const API_URL = process.env.REACT_APP_API_URL;

interface SpecialPackagesManagementProps {
  active: string;
}

const SpecialPackagesManagement: React.FC<SpecialPackagesManagementProps> = ({ active }) => {
  const [packages, setPackages] = useState<SpecialPackage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [editingPackage, setEditingPackage] = useState<SpecialPackage | null>(null);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [uploading, setUploading] = useState<boolean>(false);
  const [dayPlans, setDayPlans] = useState<DayPlan[]>([{
    dayNumber: 1,
    title: '',
    description: '',
    activities: '',
    meals: {
      breakfast: false,
      lunch: false,
      dinner: false,
      details: ''
    }
  }]);
  const [form] = Form.useForm();
  const hasFetchedRef = useRef<boolean>(false);

  useEffect(() => {
    if (active === 'special-packages' && !hasFetchedRef.current) {
      fetchPackages();
      hasFetchedRef.current = true;
    }
  }, [active]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await specialPackageAPI.getAllAdmin();
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Error fetching special packages:', error);
      message.error('Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPackage(null);
    setImageUrl('');
    setDayPlans([{
      dayNumber: 1,
      title: '',
      description: '',
      activities: '',
      meals: { breakfast: false, lunch: false, dinner: false, details: '' }
    }]);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (pkg: SpecialPackage) => {
    setEditingPackage(pkg);
    setImageUrl(pkg.image || '');
    setDayPlans(pkg.dayPlans && pkg.dayPlans.length > 0 ? pkg.dayPlans : [{
      dayNumber: 1,
      title: '',
      description: '',
      activities: '',
      meals: { breakfast: false, lunch: false, dinner: false, details: '' }
    }]);
    form.setFieldsValue({
      name: pkg.name,
      description: pkg.description,
      destination: pkg.destination,
      days: pkg.days,
      nights: pkg.nights,
      standardPrice: pkg.standardPrice,
      luxuryPrice: pkg.luxuryPrice,
      discount: pkg.discount || 0,
      showPrice: pkg.showPrice !== undefined ? pkg.showPrice : true,
      includes: pkg.includes.join('\n'),
      excludes: pkg.excludes.join('\n'),
      image: pkg.image
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await specialPackageAPI.delete(id);
      message.success('Package deleted successfully');
      fetchPackages();
    } catch (error) {
      console.error('Error deleting package:', error);
      message.error('Failed to delete package');
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await specialPackageAPI.toggle(id);
      message.success('Package visibility updated');
      fetchPackages();
    } catch (error) {
      console.error('Error toggling package:', error);
      message.error('Failed to update package');
    }
  };

  const handleTogglePrice = async (id: string, showPrice: boolean) => {
    try {
      await specialPackageAPI.update(id, { showPrice });
      message.success(`Price ${showPrice ? 'shown' : 'hidden'} successfully`);
      fetchPackages();
    } catch (error) {
      console.error('Error toggling price:', error);
      message.error('Failed to update price visibility');
    }
  };

  // Image upload handler
  const handleImageUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('image', file);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        let uploadedImageUrl = data.imageUrl;
        if (!uploadedImageUrl.startsWith('http')) {
          uploadedImageUrl = `${API_URL}${uploadedImageUrl}`;
        }
        
        setImageUrl(uploadedImageUrl);
        form.setFieldsValue({ image: uploadedImageUrl });
        message.success('Image uploaded successfully');
        onSuccess?.(data, file as any);
      } else {
        throw new Error(data.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      message.error(error.message || 'Failed to upload image');
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  const handleImageRemove = () => {
    setImageUrl('');
    form.setFieldsValue({ image: '' });
  };

  // Day Plan Management
  const addDay = () => {
    const newDay: DayPlan = {
      dayNumber: dayPlans.length + 1,
      title: '',
      description: '',
      activities: '',
      meals: { breakfast: false, lunch: false, dinner: false, details: '' }
    };
    setDayPlans([...dayPlans, newDay]);
  };

  const removeDay = (dayNumber: number) => {
    if (dayPlans.length === 1) {
      message.warning('At least one day is required');
      return;
    }
    const updated = dayPlans
      .filter(d => d.dayNumber !== dayNumber)
      .map((d, index) => ({ ...d, dayNumber: index + 1 }));
    setDayPlans(updated);
  };

  const updateDayField = (dayNumber: number, field: keyof DayPlan, value: any) => {
    const updated = dayPlans.map(d => 
      d.dayNumber === dayNumber ? { ...d, [field]: value } : d
    );
    setDayPlans(updated);
  };

  const updateMeal = (dayNumber: number, mealType: 'breakfast' | 'lunch' | 'dinner', checked: boolean) => {
    const updated = dayPlans.map(d => 
      d.dayNumber === dayNumber 
        ? { ...d, meals: { ...d.meals, [mealType]: checked } }
        : d
    );
    setDayPlans(updated);
  };

  const updateMealDetails = (dayNumber: number, details: string) => {
    const updated = dayPlans.map(d => 
      d.dayNumber === dayNumber 
        ? { ...d, meals: { ...d.meals, details } }
        : d
    );
    setDayPlans(updated);
  };

  const handleSubmit = async (values: any) => {
    try {
      // Validate day plans
      const invalidDay = dayPlans.find(d => !d.title || !d.description);
      if (invalidDay) {
        message.error(`Day ${invalidDay.dayNumber}: Title and description are required`);
        return;
      }

      const packageData = {
        name: values.name,
        description: values.description,
        destination: values.destination,
        days: values.days,
        nights: values.nights,
        standardPrice: values.standardPrice,
        luxuryPrice: values.luxuryPrice,
        discount: values.discount || 0,
        showPrice: values.showPrice !== undefined ? values.showPrice : true,
        image: imageUrl || values.image || '',
        includes: values.includes ? values.includes.split('\n').filter((i: string) => i.trim()) : [],
        excludes: values.excludes ? values.excludes.split('\n').filter((i: string) => i.trim()) : [],
        dayPlans: dayPlans
      };

      if (editingPackage) {
        await specialPackageAPI.update(editingPackage._id, packageData);
        message.success('Package updated successfully');
      } else {
        await specialPackageAPI.create(packageData);
        message.success('Package created successfully');
      }

      setModalVisible(false);
      setImageUrl('');
      form.resetFields();
      fetchPackages();
    } catch (error) {
      console.error('Error saving package:', error);
      message.error('Failed to save package');
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      width: 100,
      render: (image: string) => (
        image ? (
          <Image
            src={image}
            alt="Package"
            width={80}
            height={60}
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
          />
        ) : (
          <div style={{ 
            width: 80, 
            height: 60, 
            background: '#f0f0f0', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            borderRadius: '4px'
          }}>
            No Image
          </div>
        )
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      key: 'destination',
    },
    {
      title: 'Duration',
      key: 'duration',
      render: (_: any, record: SpecialPackage) => `${record.days}D / ${record.nights}N`,
    },
    {
      title: 'Standard Price',
      dataIndex: 'standardPrice',
      key: 'standardPrice',
      render: (price: number, record: SpecialPackage) => (
        <div>
          {record.hasDiscount && record.discount > 0 ? (
            <div>
              <div style={{ 
                textDecoration: 'line-through', 
                color: '#999', 
                fontSize: '12px' 
              }}>
                ${price}
              </div>
              <div style={{ color: '#f5222d', fontWeight: 'bold' }}>
                ${record.discountedStandardPrice}
              </div>
            </div>
          ) : (
            <span>${price}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Luxury Price',
      dataIndex: 'luxuryPrice',
      key: 'luxuryPrice',
      render: (price: number, record: SpecialPackage) => (
        <div>
          {record.hasDiscount && record.discount > 0 ? (
            <>
              <div style={{ 
                textDecoration: 'line-through', 
                color: '#999', 
                fontSize: '12px' 
              }}>
                ${price}
              </div>
              <div style={{ color: '#f5222d', fontWeight: 'bold' }}>
                ${record.discountedLuxuryPrice}
              </div>
            </>
          ) : (
            <span>${price}</span>
          )}
        </div>
      ),
    },
    {
      title: 'Discount',
      dataIndex: 'discount',
      key: 'discount',
      render: (discount: number) => (
        discount > 0 ? (
          <Badge count={`${discount}% OFF`} style={{ backgroundColor: '#52c41a' }} />
        ) : (
          <span style={{ color: '#999' }}>-</span>
        )
      ),
    },
    {
      title: 'Price Display',
      dataIndex: 'showPrice',
      key: 'showPrice',
      width: 150,
      render: (showPrice: boolean, record: SpecialPackage) => (
        <Switch
          checked={showPrice}
          onChange={(checked) => handleTogglePrice(record._id, checked)}
          checkedChildren="Visible"
          unCheckedChildren="Hidden"
        />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'VISIBLE' : 'HIDDEN'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: SpecialPackage) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={record.isActive ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => handleToggle(record._id)}
          >
            {record.isActive ? 'Hide' : 'Show'}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this package?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        marginBottom: 16,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <Title level={3} style={{ margin: 0 }}>Special Multi-Day Packages</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Add Special Package
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={packages}
        rowKey="_id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingPackage ? 'Edit Special Package' : 'Add Special Package'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setImageUrl('');
          form.resetFields();
        }}
        footer={null}
        width="95%"
        style={{ maxWidth: 900, top: 20 }}
        styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          {/* Image Upload */}
          <Form.Item label="Package Image">
            <div style={{ marginBottom: 16 }}>
              {imageUrl ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <Image
                    src={imageUrl}
                    alt="Package preview"
                    width={200}
                    height={150}
                    style={{ objectFit: 'cover', borderRadius: '8px' }}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={handleImageRemove}
                    style={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <Upload
                  name="image"
                  listType="picture-card"
                  showUploadList={false}
                  customRequest={handleImageUpload}
                  accept="image/*"
                  disabled={uploading}
                >
                  <div>
                    {uploading ? <LoadingOutlined /> : <UploadOutlined />}
                    <div style={{ marginTop: 8 }}>
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </div>
                  </div>
                </Upload>
              )}
            </div>
          </Form.Item>

          <Form.Item name="image" label="Or Enter Image URL">
            <Input 
              placeholder="https://example.com/image.jpg"
              onChange={(e) => setImageUrl(e.target.value)}
            />
          </Form.Item>

          {/* Basic Package Info */}
          <Form.Item
            name="name"
            label="Package Name"
            rules={[{ required: true, message: 'Please enter package name' }]}
          >
            <Input placeholder="e.g., Coastal Paradise - 3 Days Tour" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Package Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Describe the overall tour experience..." 
            />
          </Form.Item>

          <Form.Item
            name="destination"
            label="Destination"
            rules={[{ required: true, message: 'Please enter destination' }]}
          >
            <Input placeholder="e.g., Galle, Mirissa, Bentota" />
          </Form.Item>

          {/* Duration */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="days"
              label="Number of Days"
              rules={[
                { required: true, message: 'Please enter number of days' },
                { type: 'number', min: 2, message: 'Minimum 2 days' }
              ]}
            >
              <InputNumber
                min={2}
                style={{ width: '100%' }}
                placeholder="e.g., 3"
              />
            </Form.Item>

            <Form.Item
              name="nights"
              label="Number of Nights"
              rules={[
                { required: true, message: 'Please enter number of nights' },
                { type: 'number', min: 1, message: 'Minimum 1 night' }
              ]}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="e.g., 2"
              />
            </Form.Item>
          </div>

          {/* Pricing */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <Form.Item
              name="standardPrice"
              label="Standard Price (per person)"
              rules={[{ required: true, message: 'Please enter standard price' }]}
            >
              <InputNumber
                min={0}
                prefix="$"
                style={{ width: '100%' }}
                placeholder="e.g., 500"
              />
            </Form.Item>

            <Form.Item
              name="luxuryPrice"
              label="Luxury Price (per person)"
              rules={[{ required: true, message: 'Please enter luxury price' }]}
            >
              <InputNumber
                min={0}
                prefix="$"
                style={{ width: '100%' }}
                placeholder="e.g., 800"
              />
            </Form.Item>
          </div>

          <Form.Item
            name="discount"
            label="Discount (%)"
            help="Enter discount percentage (0-100). Leave 0 for no discount. Applies to both Standard and Luxury prices."
          >
            <InputNumber<number>
              style={{ width: '100%' }}
              min={0}
              max={100}
              prefix={<PercentageOutlined />}
              placeholder="e.g., 20"
              formatter={value => `${value}%`}
              parser={value => {
                const parsed = value?.replace('%', '');
                return parsed ? Number(parsed) : 0;
              }}
            />
          </Form.Item>

          <Form.Item
            name="showPrice"
            label="Show Prices to Customers"
            valuePropName="checked"
            initialValue={true}
            help="When disabled, customers will see 'Contact for Price' instead of actual prices"
          >
            <Switch
              checkedChildren="Visible"
              unCheckedChildren="Hidden"
            />
          </Form.Item>

          {/* Includes/Excludes */}
          <Form.Item
            name="includes"
            label="Package Includes (one per line)"
          >
            <TextArea
              rows={4}
              placeholder="Hotel accommodation&#10;All meals&#10;Transportation&#10;Tour guide&#10;Entrance fees"
            />
          </Form.Item>

          <Form.Item
            name="excludes"
            label="Package Excludes (one per line)"
          >
            <TextArea
              rows={3}
              placeholder="Personal expenses&#10;Travel insurance&#10;Tips"
            />
          </Form.Item>

          <Divider />

          {/* Day Plans Section */}
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <Title level={5} style={{ margin: 0 }}>Day-by-Day Itinerary</Title>
              <Button
                type="dashed"
                onClick={addDay}
                icon={<PlusOutlined />}
              >
                Add Day
              </Button>
            </div>

            {dayPlans.map((day, index) => (
              <Card
                key={day.dayNumber}
                style={{ marginBottom: '16px' }}
                size="small"
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong>Day {day.dayNumber}</Text>
                    {dayPlans.length > 1 && (
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<MinusCircleOutlined />}
                        onClick={() => removeDay(day.dayNumber)}
                      >
                        Remove Day
                      </Button>
                    )}
                  </div>
                }
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text strong>Day Title *</Text>
                    <Input
                      placeholder="e.g., Arrival & Galle Fort"
                      value={day.title}
                      onChange={(e) => updateDayField(day.dayNumber, 'title', e.target.value)}
                      style={{ marginTop: '4px' }}
                    />
                  </div>

                  <div>
                    <Text strong>Description *</Text>
                    <TextArea
                      rows={3}
                      placeholder="Describe what happens on this day..."
                      value={day.description}
                      onChange={(e) => updateDayField(day.dayNumber, 'description', e.target.value)}
                      style={{ marginTop: '4px' }}
                    />
                  </div>

                  <div>
                    <Text strong>Activities/Locations</Text>
                    <Input
                      placeholder="e.g., Galle Fort, Dutch Museum, Lighthouse"
                      value={day.activities}
                      onChange={(e) => updateDayField(day.dayNumber, 'activities', e.target.value)}
                      style={{ marginTop: '4px' }}
                    />
                  </div>

                  <div>
                    <Text strong>Meals Included</Text>
                    <div style={{ marginTop: '8px' }}>
                      <Space>
                        <Checkbox
                          checked={day.meals.breakfast}
                          onChange={(e) => updateMeal(day.dayNumber, 'breakfast', e.target.checked)}
                        >
                          Breakfast
                        </Checkbox>
                        <Checkbox
                          checked={day.meals.lunch}
                          onChange={(e) => updateMeal(day.dayNumber, 'lunch', e.target.checked)}
                        >
                          Lunch
                        </Checkbox>
                        <Checkbox
                          checked={day.meals.dinner}
                          onChange={(e) => updateMeal(day.dayNumber, 'dinner', e.target.checked)}
                        >
                          Dinner
                        </Checkbox>
                      </Space>
                    </div>
                  </div>

                  {(day.meals.breakfast || day.meals.lunch || day.meals.dinner) && (
                    <div>
                      <Text strong>Meal Details (Optional)</Text>
                      <Input
                        placeholder="e.g., Lunch at Ocean View Restaurant"
                        value={day.meals.details}
                        onChange={(e) => updateMealDetails(day.dayNumber, e.target.value)}
                        style={{ marginTop: '4px' }}
                      />
                    </div>
                  )}
                </Space>
              </Card>
            ))}
          </div>

          <Divider />

          {/* Submit Button */}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={uploading}>
                {editingPackage ? 'Update' : 'Create'} Package
              </Button>
              <Button onClick={() => {
                setModalVisible(false);
                setImageUrl('');
                form.resetFields();
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SpecialPackagesManagement;